import React, { useState, useEffect, useMemo } from 'react';
import { 
  Database, Users, TrendingUp, TrendingDown, DollarSign, AlertTriangle,
  MessageCircle, UserCheck, Settings, Plus, Edit2, Trash2,
  Download, Upload, Search, Target, History, Eye,
  ArrowUpRight, ArrowDownRight, Phone, Crown, Star, Award,
  BarChart3, Activity, Clock, ChevronDown, ChevronLeft, ChevronRight,
  FileSpreadsheet, FileJson, X, Check, CalendarIcon, Layers,
  Package, Cpu, CheckCircle2, XCircle, Timer, Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, subDays, startOfWeek, startOfMonth, isWithinInterval, parseISO, startOfDay, endOfDay, isSameDay } from 'date-fns';

import SegmentationIntelligence from './SegmentationIntelligence';
import { usePersistedState } from '@/hooks/usePersistedState';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section Header Component
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <p className="text-xs text-[#9CA3AF]">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({ icon: Icon, label, value, sublabel, color, onClick, highlight = false, clickable = false }) {
  return (
    <div 
      className={`bg-[hsl(var(--surface-elevated))] border rounded-xl p-4 transition-all ${clickable ? 'cursor-pointer hover:scale-[1.02]' : ''} ${highlight ? 'border-[#E63946]/50 shadow-[0_0_20px_-5px_#E63946]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]'}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20`, border: `1px solid ${color}40` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</p>
      </div>
      <p className="text-2xl font-mono font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sublabel && <p className="text-xs text-[#9CA3AF] mt-1">{sublabel}</p>}
      {clickable && <p className="text-[10px] text-[#6B7280] mt-2">Click to view list →</p>}
    </div>
  );
}

// Loyalty & Status Badges - Updated: Super Elite -> High Council
function LoyaltyBadge({ tier }) {
  const tiers = { 
    regular: { label: 'Regular', color: '#6B7280', icon: Users }, 
    vip: { label: 'VIP', color: '#F59E0B', icon: Star }, 
    elite: { label: 'Elite', color: '#8B5CF6', icon: Award }, 
    highCouncil: { label: 'High Council', color: '#E63946', icon: Crown } 
  };
  const config = tiers[tier] || tiers.regular;
  const IconComp = config.icon;
  return <Badge className="text-[10px] px-2 py-0.5 flex items-center gap-1" style={{ backgroundColor: `${config.color}20`, color: config.color }}><IconComp size={10} />{config.label}</Badge>;
}

function StatusBadge({ status }) {
  const statuses = { active: { label: 'Active', color: '#10B981' }, inactive: { label: 'Inactive', color: '#6B7280' }, mc: { label: 'MC', color: '#E63946' }, loss: { label: 'Loss >50%', color: '#F59E0B' }, noTrade: { label: 'No Trade', color: '#3B82F6' }, growing: { label: 'Growing', color: '#10B981' } };
  const config = statuses[status] || statuses.inactive;
  return <Badge className="text-[10px] px-2 py-0.5" style={{ backgroundColor: `${config.color}20`, color: config.color }}>{config.label}</Badge>;
}

// Export to CSV utility
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  link.click();
};

// Export to Excel (simple xlsx format)
const exportToExcel = (data, filename) => {
  // For simplicity, export as CSV with .xlsx extension (opens in Excel)
  exportToCSV(data, filename.replace('.csv', '.xlsx'));
};

export default function DatabaseSegmentationContent({ ib }) {
  // Broker config
  const defaultBrokerConfig = { brokerA: { name: 'Octa', enabled: true }, brokerB: { name: 'HFM', enabled: true }, brokerC: { name: 'Vantage', enabled: true }, brokerD: { name: '', enabled: false }, brokerE: { name: '', enabled: false } };
  const [mainView, setMainView] = usePersistedState(`segmentation:${ib?.id || 'none'}:view`, 'intelligence');
  const [brokerConfig, setBrokerConfig] = useState(defaultBrokerConfig);
  const enabledBrokers = Object.entries(brokerConfig).filter(([_, config]) => config.enabled && config.name);
  
  // States
  const [overviewBroker, setOverviewBroker] = useState('all');
  const [tableBroker, setTableBroker] = useState('brokerA');
  const [dateRange, setDateRange] = useState('all');
  const [customDateStart, setCustomDateStart] = useState(null);
  const [customDateEnd, setCustomDateEnd] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [listModalType, setListModalType] = useState({ type: '', title: '', color: '', clients: [] });
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLoyalty, setFilterLoyalty] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  
  // New transaction form
  const [newTransaction, setNewTransaction] = useState({ type: 'deposit', amount: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
  const [newLotEntry, setNewLotEntry] = useState({ lots: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
  const [transactionTab, setTransactionTab] = useState('deposit');
  
  // Product/Indicator tracking
  const defaultProducts = ['FIRA Indicator', 'Smart Money Concept', 'Gold Scalper Pro', 'Trend Master'];
  const [productList, setProductList] = useState(defaultProducts);
  const [newProductName, setNewProductName] = useState('');
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProductAssignment, setNewProductAssignment] = useState({ product: '', tvUsername: '', installDate: format(new Date(), 'yyyy-MM-dd'), status: 'pending', notes: '' });
  const [filterProduct, setFilterProduct] = useState('all');
  
  // Tier & PM settings
  const defaultTierSettings = { vip: { minDeposit: 5000, minLots: 50, minWithdraw: 1000 }, elite: { minDeposit: 20000, minLots: 200, minWithdraw: 5000 }, highCouncil: { minDeposit: 50000, minLots: 500, minWithdraw: 15000 } };
  const [tierSettings, setTierSettings] = useState(defaultTierSettings);
  const defaultPMCriteria = { lossThreshold: 50, noTradeDays: 3, growthThreshold: 50 };
  const [pmCriteria, setPMCriteria] = useState(defaultPMCriteria);
  
  // Client database
  const [clientDatabase, setClientDatabase] = useState({ brokerA: [], brokerB: [], brokerC: [] });
  
  // New client form
  const [newClient, setNewClient] = useState({ name: '', accountNo: '', accountType: 'Standard', phone: '', regDate: format(new Date(), 'yyyy-MM-dd'), balance: '', status: 'active', notes: '', initialDeposit: '', clientType: 'new', tvUsername: '', telegramId: '', product: 'none', statusIndi: 'pending' });
  
  // Track if data loaded from MongoDB
  const [dataLoaded, setDataLoaded] = useState(false);
  const [editMeta, setEditMeta] = useState({});
  
  // Load ALL segmentation data from MongoDB on mount
  useEffect(() => {
    if (!ib?.id) return;
    const fetchAll = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [clientRes, settingsRes, brokerRes] = await Promise.all([
          axios.get(`${API}/ib-data/${ib.id}/segmentation_clients`, { headers }).catch(() => ({ data: { data: null } })),
          axios.get(`${API}/ib-data/${ib.id}/segmentation_settings`, { headers }).catch(() => ({ data: { data: null } })),
          axios.get(`${API}/ib-data/${ib.id}/broker_config`, { headers }).catch(() => ({ data: { data: null } })),
        ]);
        
        if (clientRes.data?.data) {
          const d = clientRes.data.data;
          if (d.clientDatabase) setClientDatabase(d.clientDatabase);
          if (d.productList) setProductList(d.productList);
        }
        if (settingsRes.data?.data) {
          const s = settingsRes.data.data;
          if (s.tierSettings) {
            const ts = s.tierSettings;
            if (ts.superElite && !ts.highCouncil) { ts.highCouncil = ts.superElite; delete ts.superElite; }
            setTierSettings(ts);
          }
          if (s.pmCriteria) setPMCriteria(s.pmCriteria);
        }
        if (brokerRes.data?.data?.brokerConfig) {
          setBrokerConfig(brokerRes.data.data.brokerConfig);
        }
        // Get latest edit metadata from any of the responses
        const latestMeta = [clientRes.data, settingsRes.data, brokerRes.data]
          .filter(d => d?.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
        setDataLoaded(true);
        if (latestMeta?.updated_by_name) {
          setEditMeta({ name: latestMeta.updated_by_name, time: latestMeta.updated_at });
        }
      } catch (e) { console.error('Failed to load segmentation data:', e); }
      
    };
    fetchAll();
  }, [ib?.id]);
  
  // Explicit save functions — only called after user actions
  const saveClientData = React.useCallback((clients, products) => {
    if (!ib?.id) return;
    const token = localStorage.getItem('token');
    axios.put(`${API}/ib-data/${ib.id}/segmentation_clients`, { data: { clientDatabase: clients, productList: products } }, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => console.error("Save failed:", e));
  }, [ib?.id]);
  
  const saveSettings = React.useCallback((tiers, pm) => {
    if (!ib?.id) return;
    const token = localStorage.getItem('token');
    axios.put(`${API}/ib-data/${ib.id}/segmentation_settings`, { data: { tierSettings: tiers, pmCriteria: pm } }, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => console.error("Save failed:", e));
  }, [ib?.id]);
  
  // Calculate TOTAL (all time) from transactions - for tier calculation
  const calcTotalDeposit = (transactions) => transactions?.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0) || 0;
  const calcTotalWithdraw = (transactions) => transactions?.filter(t => t.type === 'withdraw').reduce((sum, t) => sum + t.amount, 0) || 0;
  const calcTotalLots = (lotEntries) => lotEntries?.reduce((sum, e) => sum + e.lots, 0) || 0;
  const calcDepositFreq = (transactions) => transactions?.filter(t => t.type === 'deposit').length || 0;
  const calcWithdrawFreq = (transactions) => transactions?.filter(t => t.type === 'withdraw').length || 0;
  
  // Get date range based on selection
  const getDateRange = () => {
    const now = new Date();
    const today = startOfDay(now);
    switch (dateRange) {
      case 'today': return { start: today, end: endOfDay(now) };
      case 'yesterday': return { start: startOfDay(subDays(now, 1)), end: endOfDay(subDays(now, 1)) };
      case 'week': return { start: startOfWeek(now), end: endOfDay(now) };
      case 'month': return { start: startOfMonth(now), end: endOfDay(now) };
      case 'custom': 
        if (customDateStart && customDateEnd) {
          return { start: startOfDay(customDateStart), end: endOfDay(customDateEnd) };
        }
        return null;
      default: return null; // 'all'
    }
  };
  
  // Calculate PERIOD totals - ONLY for selected date range
  const calcPeriodDeposit = (transactions, range) => {
    if (!range) return calcTotalDeposit(transactions); // All time
    return transactions?.filter(t => t.type === 'deposit' && isWithinInterval(parseISO(t.date), range)).reduce((sum, t) => sum + t.amount, 0) || 0;
  };
  
  const calcPeriodWithdraw = (transactions, range) => {
    if (!range) return calcTotalWithdraw(transactions); // All time
    return transactions?.filter(t => t.type === 'withdraw' && isWithinInterval(parseISO(t.date), range)).reduce((sum, t) => sum + t.amount, 0) || 0;
  };
  
  const calcPeriodLots = (lotEntries, range) => {
    if (!range) return calcTotalLots(lotEntries); // All time
    return lotEntries?.filter(e => isWithinInterval(parseISO(e.date), range)).reduce((sum, e) => sum + e.lots, 0) || 0;
  };
  
  const hasPeriodActivity = (client, range) => {
    if (!range) return true;
    const hasTransaction = client.transactions?.some(t => isWithinInterval(parseISO(t.date), range)) || false;
    const hasLot = client.lotEntries?.some(e => isWithinInterval(parseISO(e.date), range)) || false;
    return hasTransaction || hasLot;
  };
  
  // Tier calculation - Uses TOTAL (all time) deposit and lots
  const calculateTier = (client) => {
    const deposit = calcTotalDeposit(client.transactions);
    const lots = calcTotalLots(client.lotEntries);
    if (deposit >= tierSettings.highCouncil.minDeposit || lots >= tierSettings.highCouncil.minLots) return 'highCouncil';
    if (deposit >= tierSettings.elite.minDeposit || lots >= tierSettings.elite.minLots) return 'elite';
    if (deposit >= tierSettings.vip.minDeposit || lots >= tierSettings.vip.minLots) return 'vip';
    return 'regular';
  };
  
  // Get clients
  const getAllClients = () => Object.values(clientDatabase).flat();
  const getClientsForOverview = () => overviewBroker === 'all' ? getAllClients() : (clientDatabase[overviewBroker] || []);
  const getClientsForTable = () => clientDatabase[tableBroker] || [];
  
  // Product helpers
  const getClientProducts = (client) => client.products || [];
  const hasActiveProduct = (client) => getClientProducts(client).some(p => p.status === 'installed' || p.status === 'active');
  const hasPendingProduct = (client) => getClientProducts(client).some(p => p.status === 'pending');
  
  // Product handler
  const handleAddProductToClient = () => {
    if (!viewingClient || !newProductAssignment.product) return;
    const updatedClient = {
      ...viewingClient,
      products: [
        ...(viewingClient.products || []),
        { id: Date.now(), ...newProductAssignment }
      ]
    };
    Object.keys(clientDatabase).forEach(brokerKey => {
      if (clientDatabase[brokerKey].some(c => c.id === viewingClient.id)) {
        setClientDatabase(prev => ({
          ...prev,
          [brokerKey]: prev[brokerKey].map(c => c.id === viewingClient.id ? updatedClient : c)
        }));
      }
    });
    setViewingClient(updatedClient);
    setNewProductAssignment({ product: '', tvUsername: '', installDate: format(new Date(), 'yyyy-MM-dd'), status: 'pending', notes: '' });
    setShowAddProductModal(false);
  };
  
  const handleUpdateProductStatus = (productId, newStatus) => {
    if (!viewingClient) return;
    const updatedClient = {
      ...viewingClient,
      products: (viewingClient.products || []).map(p => 
        p.id === productId ? { ...p, status: newStatus, ...(newStatus === 'installed' ? { installDate: format(new Date(), 'yyyy-MM-dd') } : {}) } : p
      )
    };
    Object.keys(clientDatabase).forEach(brokerKey => {
      if (clientDatabase[brokerKey].some(c => c.id === viewingClient.id)) {
        setClientDatabase(prev => ({
          ...prev,
          [brokerKey]: prev[brokerKey].map(c => c.id === viewingClient.id ? updatedClient : c)
        }));
      }
    });
    setViewingClient(updatedClient);
  };
  
  const handleRemoveProduct = (productId) => {
    if (!viewingClient) return;
    const updatedClient = {
      ...viewingClient,
      products: (viewingClient.products || []).filter(p => p.id !== productId)
    };
    Object.keys(clientDatabase).forEach(brokerKey => {
      if (clientDatabase[brokerKey].some(c => c.id === viewingClient.id)) {
        setClientDatabase(prev => ({
          ...prev,
          [brokerKey]: prev[brokerKey].map(c => c.id === viewingClient.id ? updatedClient : c)
        }));
      }
    });
    setViewingClient(updatedClient);
  };
  
  // Metrics - Always use ALL TIME for overview
  const overviewClients = getClientsForOverview();
  const metrics = useMemo(() => ({
    totalClients: overviewClients.length,
    activeClients: overviewClients.filter(c => c.status === 'active' || c.status === 'growing').length,
    mcClients: overviewClients.filter(c => c.status === 'mc').length,
    lossClients: overviewClients.filter(c => c.status === 'loss').length,
    noTradeClients: overviewClients.filter(c => c.status === 'noTrade' || calcTotalLots(c.lotEntries) === 0).length,
    totalDeposit: overviewClients.reduce((sum, c) => sum + calcTotalDeposit(c.transactions), 0),
    totalWithdraw: overviewClients.reduce((sum, c) => sum + calcTotalWithdraw(c.transactions), 0),
    totalLots: overviewClients.reduce((sum, c) => sum + calcTotalLots(c.lotEntries), 0),
    totalBalance: overviewClients.reduce((sum, c) => sum + (c.balance || 0), 0),
    needPMLoss: overviewClients.filter(c => c.status === 'mc' || c.status === 'loss'),
    needPMTrade: overviewClients.filter(c => c.status === 'noTrade' || (calcTotalDeposit(c.transactions) > 0 && calcTotalLots(c.lotEntries) === 0)),
    needPMWithdraw: overviewClients.filter(c => c.status === 'growing'),
    regularClients: overviewClients.filter(c => calculateTier(c) === 'regular'),
    vipClients: overviewClients.filter(c => calculateTier(c) === 'vip'),
    eliteClients: overviewClients.filter(c => calculateTier(c) === 'elite'),
    highCouncilClients: overviewClients.filter(c => calculateTier(c) === 'highCouncil'),
    // Product/Indicator metrics
    clientsWithProducts: overviewClients.filter(c => (c.products || []).length > 0),
    productsInstalled: overviewClients.filter(c => (c.products || []).some(p => p.status === 'installed' || p.status === 'active')),
    productsPending: overviewClients.filter(c => (c.products || []).some(p => p.status === 'pending')),
    productsExpired: overviewClients.filter(c => (c.products || []).some(p => p.status === 'expired')),
  }), [overviewClients, tierSettings]);
  
  // Filter and sort table clients
  const dateRangeValues = getDateRange();
  const filteredAndSortedClients = useMemo(() => {
    let clients = getClientsForTable().filter(client => {
      const matchesSearch = client.name.toLowerCase().includes(searchQuery.toLowerCase()) || client.accountNo.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
      const matchesLoyalty = filterLoyalty === 'all' || calculateTier(client) === filterLoyalty;
      const matchesProduct = filterProduct === 'all' || 
        (filterProduct === 'none' ? (client.products || []).length === 0 : 
         filterProduct === 'pending' ? (client.products || []).some(p => p.status === 'pending') :
         filterProduct === 'installed' ? (client.products || []).some(p => p.status === 'installed' || p.status === 'active') :
         (client.products || []).some(p => p.product === filterProduct));
      return matchesSearch && matchesStatus && matchesLoyalty && matchesProduct;
    });
    
    // Sort: clients with activity in period first, then by period deposit descending
    if (dateRangeValues) {
      clients.sort((a, b) => {
        const aHasActivity = hasPeriodActivity(a, dateRangeValues);
        const bHasActivity = hasPeriodActivity(b, dateRangeValues);
        if (aHasActivity && !bHasActivity) return -1;
        if (!aHasActivity && bHasActivity) return 1;
        const aDepo = calcPeriodDeposit(a.transactions, dateRangeValues);
        const bDepo = calcPeriodDeposit(b.transactions, dateRangeValues);
        return bDepo - aDepo;
      });
    } else {
      clients.sort((a, b) => calcTotalDeposit(b.transactions) - calcTotalDeposit(a.transactions));
    }
    
    return clients;
  }, [clientDatabase, tableBroker, searchQuery, filterStatus, filterLoyalty, filterProduct, dateRange, customDateStart, customDateEnd]);
  
  // Pagination
  const totalPages = Math.ceil(filteredAndSortedClients.length / itemsPerPage);
  const paginatedClients = filteredAndSortedClients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  useEffect(() => { setCurrentPage(1); }, [tableBroker, searchQuery, filterStatus, filterLoyalty, filterProduct, dateRange, itemsPerPage, customDateStart, customDateEnd]);
  
  // CRUD operations
  const handleAddClient = () => {
    if (!newClient.name || !newClient.accountNo) return;
    const initialDepo = parseFloat(newClient.initialDeposit) || 0;
    const client = {
      ...newClient,
      id: Date.now(),
      product: newClient.product === 'none' ? '' : newClient.product,
      balance: parseFloat(newClient.balance) || initialDepo,
      transactions: initialDepo > 0 ? [{ id: 1, type: 'deposit', amount: initialDepo, date: newClient.regDate, note: 'Initial deposit' }] : [],
      lotEntries: [],
      products: [],
    };
    setClientDatabase(prev => ({ ...prev, [tableBroker]: [...(prev[tableBroker] || []), client] }));
    setNewClient({ name: '', accountNo: '', accountType: 'Standard', phone: '', regDate: format(new Date(), 'yyyy-MM-dd'), balance: '', status: 'active', notes: '', initialDeposit: '', clientType: 'new', tvUsername: '', telegramId: '', product: 'none', statusIndi: 'pending' });
    setShowAddModal(false);
  };
  
  const handleEditClient = () => {
    if (!editingClient) return;
    setClientDatabase(prev => ({
      ...prev,
      [tableBroker]: prev[tableBroker].map(c => c.id === editingClient.id ? { ...editingClient, balance: parseFloat(editingClient.balance) || 0 } : c)
    }));
    setShowEditModal(false);
    setEditingClient(null);
  };
  
  const handleDeleteClient = (clientId) => {
    setClientDatabase(prev => ({ ...prev, [tableBroker]: prev[tableBroker].filter(c => c.id !== clientId) }));
  };
  
  // Add transaction to viewing client
  const handleAddTransaction = () => {
    if (!viewingClient || !newTransaction.amount) return;
    const amount = parseFloat(newTransaction.amount);
    if (isNaN(amount) || amount <= 0) return;
    
    const updatedClient = {
      ...viewingClient,
      transactions: [
        ...viewingClient.transactions,
        { id: Date.now(), type: newTransaction.type, amount, date: newTransaction.date, note: newTransaction.note }
      ]
    };
    
    // Find which broker this client belongs to
    Object.keys(clientDatabase).forEach(brokerKey => {
      if (clientDatabase[brokerKey].some(c => c.id === viewingClient.id)) {
        setClientDatabase(prev => ({
          ...prev,
          [brokerKey]: prev[brokerKey].map(c => c.id === viewingClient.id ? updatedClient : c)
        }));
      }
    });
    
    setViewingClient(updatedClient);
    setNewTransaction({ type: 'deposit', amount: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
    setShowAddTransactionModal(false);
  };
  
  // Add lot entry to viewing client
  const handleAddLotEntry = () => {
    if (!viewingClient || !newLotEntry.lots) return;
    const lots = parseFloat(newLotEntry.lots);
    if (isNaN(lots) || lots <= 0) return;
    
    const updatedClient = {
      ...viewingClient,
      lotEntries: [
        ...(viewingClient.lotEntries || []),
        { id: Date.now(), lots, date: newLotEntry.date, note: newLotEntry.note }
      ]
    };
    
    Object.keys(clientDatabase).forEach(brokerKey => {
      if (clientDatabase[brokerKey].some(c => c.id === viewingClient.id)) {
        setClientDatabase(prev => ({
          ...prev,
          [brokerKey]: prev[brokerKey].map(c => c.id === viewingClient.id ? updatedClient : c)
        }));
      }
    });
    
    setViewingClient(updatedClient);
    setNewLotEntry({ lots: '', date: format(new Date(), 'yyyy-MM-dd'), note: '' });
    setShowAddTransactionModal(false);
  };
  
  const openListModal = (type, title, color, clients) => { setListModalType({ type, title, color, clients }); setShowListModal(true); };
  const openClientProfile = (client) => { setViewingClient(client); setShowProfileModal(true); };
  
  // Export list modal data
  const handleExportListToCSV = () => {
    if (!listModalType.clients?.length) return;
    const data = listModalType.clients.map(c => ({
      Name: c.name,
      AccountNo: c.accountNo,
      Phone: c.phone,
      Balance: c.balance,
      TotalDeposit: calcTotalDeposit(c.transactions),
      TotalWithdraw: calcTotalWithdraw(c.transactions),
      TotalLots: calcTotalLots(c.lotEntries),
      Status: c.status,
      ClientType: c.clientType || '',
      UsernameTV: c.tvUsername || '',
      TelegramID: c.telegramId || '',
      Product: c.product || '',
      StatusIndi: c.statusIndi || '',
      ProductsAssigned: (c.products || []).map(p => p.product).join(', ')
    }));
    exportToCSV(data, listModalType.title.replace(/\s+/g, '_'));
  };
  
  const handleExportListToExcel = () => {
    if (!listModalType.clients?.length) return;
    const data = listModalType.clients.map(c => ({
      Name: c.name,
      AccountNo: c.accountNo,
      Phone: c.phone,
      Balance: c.balance,
      TotalDeposit: calcTotalDeposit(c.transactions),
      TotalWithdraw: calcTotalWithdraw(c.transactions),
      TotalLots: calcTotalLots(c.lotEntries),
      Status: c.status,
      ClientType: c.clientType || '',
      UsernameTV: c.tvUsername || '',
      TelegramID: c.telegramId || '',
      Product: c.product || '',
      StatusIndi: c.statusIndi || '',
      ProductsAssigned: (c.products || []).map(p => p.product).join(', ')
    }));
    exportToExcel(data, listModalType.title.replace(/\s+/g, '_'));
  };
  
  // Date filter label
  const getDateFilterLabel = () => {
    if (dateRange === 'all') return 'All Time';
    if (dateRange === 'today') return 'Today';
    if (dateRange === 'yesterday') return 'Yesterday';
    if (dateRange === 'week') return 'This Week';
    if (dateRange === 'month') return 'This Month';
    if (dateRange === 'custom' && customDateStart && customDateEnd) {
      return `${format(customDateStart, 'dd MMM')} - ${format(customDateEnd, 'dd MMM')}`;
    }
    return 'Select Date';
  };
  
  if (mainView === 'intelligence') {
    return (
      <div className="p-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Database Segmentation</h1>
            <p className="text-sm text-[#9CA3AF]">Client intelligence dari report broker — siapa trade, siapa loss, siapa depo tak trade</p>
          </div>
          <div className="flex items-center gap-1 p-0.5 rounded-lg border border-[hsl(var(--border))]">
            <button onClick={() => setMainView('intelligence')} data-testid="seg-view-intelligence"
              className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-[#E63946] text-white">
              Client Intelligence
            </button>
            <button onClick={() => setMainView('manual')} data-testid="seg-view-manual"
              className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] hover:text-white">
              Manual Database
            </button>
          </div>
        </div>
        <SegmentationIntelligence ib={ib} />
      </div>
    );
  }

  return (
    <>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Database Segmentation</h1>
            <p className="text-sm text-[#9CA3AF]">Client database management & relationship tracking</p>
            <LastEditedBy name={editMeta.name} timestamp={editMeta.time} />
          </div>
          <div className="flex items-center gap-1 p-0.5 rounded-lg border border-[hsl(var(--border))]">
            <button onClick={() => setMainView('intelligence')} data-testid="seg-view-intelligence"
              className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] hover:text-white">
              Client Intelligence
            </button>
            <button onClick={() => setMainView('manual')} data-testid="seg-view-manual"
              className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-[#E63946] text-white">
              Manual Database
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-36 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                <CalendarIcon size={14} className="mr-2 text-[#9CA3AF]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                <SelectItem value="all" className="text-white">All Time</SelectItem>
                <SelectItem value="today" className="text-white">Today</SelectItem>
                <SelectItem value="yesterday" className="text-white">Yesterday</SelectItem>
                <SelectItem value="week" className="text-white">This Week</SelectItem>
                <SelectItem value="month" className="text-white">This Month</SelectItem>
                <SelectItem value="custom" className="text-white">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            {dateRange === 'custom' && (
              <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="border-[hsl(var(--border))] text-white">
                    <CalendarIcon size={14} className="mr-2" />
                    {customDateStart && customDateEnd ? `${format(customDateStart, 'dd/MM')} - ${format(customDateEnd, 'dd/MM')}` : 'Pick dates'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" align="end">
                  <Calendar
                    mode="range"
                    selected={{ from: customDateStart, to: customDateEnd }}
                    onSelect={(range) => {
                      setCustomDateStart(range?.from || null);
                      setCustomDateEnd(range?.to || null);
                      if (range?.from && range?.to) setShowCustomDatePicker(false);
                    }}
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            )}
            <Button variant="outline" className="border-[hsl(var(--border))] text-white" onClick={() => setShowSettingsModal(true)}>
              <Settings size={16} className="mr-2" />Settings
            </Button>
          </div>
        </div>
        
        {/* Client Overview */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader icon={BarChart3} title="Client Overview" subtitle={`${overviewBroker === 'all' ? 'All Brokers' : brokerConfig[overviewBroker]?.name} • ${metrics.totalClients} total clients`} />
          
          <div className="p-6">
            {/* Broker Tabs */}
            <div className="mb-6">
              <Tabs value={overviewBroker} onValueChange={setOverviewBroker}>
                <TabsList className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                  <TabsTrigger value="all" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E63946] data-[state=active]:to-[#F97316] data-[state=active]:text-white px-6">All</TabsTrigger>
                  {enabledBrokers.map(([key, config]) => (
                    <TabsTrigger key={key} value={key} className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E63946] data-[state=active]:to-[#F97316] data-[state=active]:text-white px-6">{config.name}</TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            
            {/* Metrics */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <MetricCard icon={Database} label="Total Database" value={metrics.totalClients} color="#3B82F6" />
              <MetricCard icon={UserCheck} label="Active Trading" value={metrics.activeClients} sublabel={`${((metrics.activeClients/metrics.totalClients)*100 || 0).toFixed(0)}% of total`} color="#10B981" />
              <MetricCard icon={AlertTriangle} label="MC / Loss" value={metrics.mcClients + metrics.lossClients} sublabel="Need attention" color="#E63946" highlight={metrics.mcClients + metrics.lossClients > 0} />
              <MetricCard icon={Clock} label="No Trade" value={metrics.noTradeClients} sublabel="Deposited but inactive" color="#F59E0B" />
              <MetricCard icon={Activity} label="Total Lots" value={metrics.totalLots.toFixed(1)} color="#8B5CF6" />
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <MetricCard icon={DollarSign} label="Total Deposit" value={`$${(metrics.totalDeposit || 0).toLocaleString()}`} color="#10B981" />
              <MetricCard icon={TrendingDown} label="Total Withdraw" value={`$${(metrics.totalWithdraw || 0).toLocaleString()}`} color="#E63946" />
              <MetricCard icon={DollarSign} label="Total Balance" value={`$${(metrics.totalBalance || 0).toLocaleString()}`} color="#F59E0B" />
            </div>
            
            {/* Follow Up Required */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold mb-3 flex items-center gap-2"><MessageCircle size={14} />FOLLOW UP REQUIRED</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#E63946]/10 border border-[#E63946]/30 rounded-xl p-4 cursor-pointer hover:border-[#E63946]/60 transition-all" onClick={() => openListModal('loss', 'PM to Calm Down', '#E63946', metrics.needPMLoss)}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#E63946]">PM to Calm Down</p>
                    <Badge className="bg-[#E63946]/20 text-[#E63946]">{metrics.needPMLoss.length}</Badge>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">Clients with MC or loss &gt;{pmCriteria.lossThreshold}%</p>
                  <p className="text-[10px] text-[#6B7280] mt-2">Click to view list →</p>
                </div>
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 cursor-pointer hover:border-[#F59E0B]/60 transition-all" onClick={() => openListModal('trade', 'PM to Start Trading', '#F59E0B', metrics.needPMTrade)}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#F59E0B]">PM to Start Trading</p>
                    <Badge className="bg-[#F59E0B]/20 text-[#F59E0B]">{metrics.needPMTrade.length}</Badge>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">Deposited but no trade in {pmCriteria.noTradeDays}+ days</p>
                  <p className="text-[10px] text-[#6B7280] mt-2">Click to view list →</p>
                </div>
                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 cursor-pointer hover:border-[#10B981]/60 transition-all" onClick={() => openListModal('withdraw', 'PM to Withdraw', '#10B981', metrics.needPMWithdraw)}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#10B981]">PM to Withdraw</p>
                    <Badge className="bg-[#10B981]/20 text-[#10B981]">{metrics.needPMWithdraw.length}</Badge>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">Account grew &gt;{pmCriteria.growthThreshold}%</p>
                  <p className="text-[10px] text-[#6B7280] mt-2">Click to view list →</p>
                </div>
              </div>
            </div>
            
            {/* Loyalty Tiers */}
            <div>
              <p className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold mb-3 flex items-center gap-2"><Crown size={14} />LOYALTY TIERS</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4 text-center cursor-pointer hover:border-[#6B7280]/60 transition-all hover:scale-[1.02]" onClick={() => openListModal('regular', 'Regular Clients', '#6B7280', metrics.regularClients)}>
                  <Users size={28} className="mx-auto mb-2 text-[#6B7280]" />
                  <p className="text-3xl font-bold text-white font-mono">{metrics.regularClients.length}</p>
                  <p className="text-base font-semibold text-[#6B7280] mt-1">Regular</p>
                </div>
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 text-center cursor-pointer hover:border-[#F59E0B]/60 transition-all hover:scale-[1.02]" onClick={() => openListModal('vip', 'VIP Clients', '#F59E0B', metrics.vipClients)}>
                  <Star size={28} className="mx-auto mb-2 text-[#F59E0B]" />
                  <p className="text-3xl font-bold text-white font-mono">{metrics.vipClients.length}</p>
                  <p className="text-base font-semibold text-[#F59E0B] mt-1">VIP</p>
                </div>
                <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-xl p-4 text-center cursor-pointer hover:border-[#8B5CF6]/60 transition-all hover:scale-[1.02]" onClick={() => openListModal('elite', 'Elite Clients', '#8B5CF6', metrics.eliteClients)}>
                  <Award size={28} className="mx-auto mb-2 text-[#8B5CF6]" />
                  <p className="text-3xl font-bold text-white font-mono">{metrics.eliteClients.length}</p>
                  <p className="text-base font-semibold text-[#8B5CF6] mt-1">Elite</p>
                </div>
                <div className="bg-[#E63946]/10 border border-[#E63946]/30 rounded-xl p-4 text-center cursor-pointer hover:border-[#E63946]/60 transition-all hover:scale-[1.02]" onClick={() => openListModal('highCouncil', 'High Council Clients', '#E63946', metrics.highCouncilClients)}>
                  <Crown size={28} className="mx-auto mb-2 text-[#E63946]" />
                  <p className="text-3xl font-bold text-white font-mono">{metrics.highCouncilClients.length}</p>
                  <p className="text-base font-semibold text-[#E63946] mt-1">High Council</p>
                </div>
              </div>
            </div>
            
            {/* Product/Indicator Tracking */}
            <div>
              <p className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold mb-3 flex items-center gap-2"><Cpu size={14} className="text-[#06B6D4]" />PRODUCT / INDICATOR TRACKING</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded-xl p-4 cursor-pointer hover:border-[#06B6D4]/60 transition-all" onClick={() => openListModal('withProducts', 'Clients with Products', '#06B6D4', metrics.clientsWithProducts)}>
                  <div className="flex items-center justify-between mb-2">
                    <Package size={22} className="text-[#06B6D4]" />
                    <Badge className="bg-[#06B6D4]/20 text-[#06B6D4] text-xs">{metrics.clientsWithProducts.length}</Badge>
                  </div>
                  <p className="text-lg font-bold text-white">{metrics.clientsWithProducts.length}</p>
                  <p className="text-xs text-[#9CA3AF]">With Products</p>
                </div>
                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 cursor-pointer hover:border-[#10B981]/60 transition-all" onClick={() => openListModal('installed', 'Installed', '#10B981', metrics.productsInstalled)}>
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle2 size={22} className="text-[#10B981]" />
                    <Badge className="bg-[#10B981]/20 text-[#10B981] text-xs">{metrics.productsInstalled.length}</Badge>
                  </div>
                  <p className="text-lg font-bold text-white">{metrics.productsInstalled.length}</p>
                  <p className="text-xs text-[#9CA3AF]">Installed</p>
                </div>
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 cursor-pointer hover:border-[#F59E0B]/60 transition-all" onClick={() => openListModal('pendingProduct', 'Pending Install', '#F59E0B', metrics.productsPending)}>
                  <div className="flex items-center justify-between mb-2">
                    <Timer size={22} className="text-[#F59E0B]" />
                    <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] text-xs">{metrics.productsPending.length}</Badge>
                  </div>
                  <p className="text-lg font-bold text-white">{metrics.productsPending.length}</p>
                  <p className="text-xs text-[#9CA3AF]">Pending Install</p>
                </div>
                <div className="bg-[#6B7280]/10 border border-[#6B7280]/30 rounded-xl p-4 cursor-pointer hover:border-[#6B7280]/60 transition-all" onClick={() => openListModal('noProducts', 'No Products', '#6B7280', overviewClients.filter(c => !(c.products||[]).length))}>
                  <div className="flex items-center justify-between mb-2">
                    <XCircle size={22} className="text-[#6B7280]" />
                    <Badge className="bg-[#6B7280]/20 text-[#6B7280] text-xs">{overviewClients.filter(c => !(c.products||[]).length).length}</Badge>
                  </div>
                  <p className="text-lg font-bold text-white">{overviewClients.filter(c => !(c.products||[]).length).length}</p>
                  <p className="text-xs text-[#9CA3AF]">No Products</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Client Database Table */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader 
            icon={Database} 
            title="Client Database" 
            subtitle={`Showing ${getDateFilterLabel()} data • ${filteredAndSortedClients.length} clients`}
            action={
              <div className="flex items-center gap-2">
                {/* Quick Date Selector in Header */}
                <div className="flex items-center gap-1 mr-2">
                  {['all', 'today', 'yesterday', 'week', 'month'].map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant={dateRange === d ? 'default' : 'outline'}
                      className={`h-7 px-3 text-xs ${dateRange === d ? 'bg-gradient-to-r from-[#E63946] to-[#F97316] text-white border-0' : 'border-[hsl(var(--border))] text-[#9CA3AF] hover:text-white'}`}
                      onClick={() => setDateRange(d)}
                    >
                      {d === 'all' ? 'All' : d === 'today' ? 'Today' : d === 'yesterday' ? 'Yesterday' : d === 'week' ? 'Week' : 'Month'}
                    </Button>
                  ))}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant={dateRange === 'custom' ? 'default' : 'outline'}
                        className={`h-7 px-3 text-xs ${dateRange === 'custom' ? 'bg-gradient-to-r from-[#E63946] to-[#F97316] text-white border-0' : 'border-[hsl(var(--border))] text-[#9CA3AF] hover:text-white'}`}
                      >
                        <CalendarIcon size={12} className="mr-1" />
                        {dateRange === 'custom' && customDateStart ? format(customDateStart, 'dd/MM') : 'Custom'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" align="end">
                      <Calendar
                        mode="range"
                        selected={{ from: customDateStart, to: customDateEnd }}
                        onSelect={(range) => {
                          setCustomDateStart(range?.from || null);
                          setCustomDateEnd(range?.to || null);
                          if (range?.from) setDateRange('custom');
                        }}
                        className="rounded-md"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[hsl(var(--border))] text-white h-8"><Upload size={14} className="mr-1.5" />Import</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <DropdownMenuItem className="text-white hover:bg-white/10"><FileSpreadsheet size={14} className="mr-2 text-[#10B981]" />CSV</DropdownMenuItem>
                    <DropdownMenuItem className="text-white hover:bg-white/10"><FileSpreadsheet size={14} className="mr-2 text-[#3B82F6]" />XLSX</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[hsl(var(--border))] text-white h-8"><Download size={14} className="mr-1.5" />Export</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <DropdownMenuItem className="text-white hover:bg-white/10" onClick={() => {
                      const data = filteredAndSortedClients.map(c => ({
                        Name: c.name, AccountNo: c.accountNo, Phone: c.phone || '', AccountType: c.accountType || '', Balance: c.balance,
                        TotalDeposit: calcTotalDeposit(c.transactions), TotalWithdraw: calcTotalWithdraw(c.transactions), TotalLots: calcTotalLots(c.lotEntries),
                        Status: c.status, ClientType: c.clientType || '', UsernameTV: c.tvUsername || '', TelegramID: c.telegramId || '',
                        Product: c.product || '', StatusIndi: c.statusIndi || '', ProductsAssigned: (c.products || []).map(p => p.product).join(', ')
                      }));
                      exportToCSV(data, `${tableBroker}_clients`);
                    }}><FileSpreadsheet size={14} className="mr-2 text-[#10B981]" />CSV</DropdownMenuItem>
                    <DropdownMenuItem className="text-white hover:bg-white/10" onClick={() => {
                      const data = filteredAndSortedClients.map(c => ({
                        Name: c.name, AccountNo: c.accountNo, Phone: c.phone || '', AccountType: c.accountType || '', Balance: c.balance,
                        TotalDeposit: calcTotalDeposit(c.transactions), TotalWithdraw: calcTotalWithdraw(c.transactions), TotalLots: calcTotalLots(c.lotEntries),
                        Status: c.status, ClientType: c.clientType || '', UsernameTV: c.tvUsername || '', TelegramID: c.telegramId || '',
                        Product: c.product || '', StatusIndi: c.statusIndi || '', ProductsAssigned: (c.products || []).map(p => p.product).join(', ')
                      }));
                      exportToExcel(data, `${tableBroker}_clients`);
                    }}><FileSpreadsheet size={14} className="mr-2 text-[#3B82F6]" />XLSX</DropdownMenuItem>
                    <DropdownMenuItem className="text-white hover:bg-white/10"><FileJson size={14} className="mr-2 text-[#F59E0B]" />JSON</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button size="sm" className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white h-8" onClick={() => setShowAddModal(true)}><Plus size={14} className="mr-1.5" />Add Client</Button>
              </div>
            }
          />
          
          <div className="p-6">
            {/* Broker Tabs */}
            <Tabs value={tableBroker} onValueChange={setTableBroker} className="w-full">
              <div className="flex items-center justify-between mb-4">
                <TabsList className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                  {enabledBrokers.map(([key, config]) => (
                    <TabsTrigger key={key} value={key} className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E63946] data-[state=active]:to-[#F97316] data-[state=active]:text-white px-6">{config.name}</TabsTrigger>
                  ))}
                </TabsList>
                
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                    <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 w-48 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9" />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-28 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="all" className="text-white">All Status</SelectItem>
                      <SelectItem value="active" className="text-white">Active</SelectItem>
                      <SelectItem value="mc" className="text-white">MC</SelectItem>
                      <SelectItem value="loss" className="text-white">Loss</SelectItem>
                      <SelectItem value="noTrade" className="text-white">No Trade</SelectItem>
                      <SelectItem value="growing" className="text-white">Growing</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterLoyalty} onValueChange={setFilterLoyalty}>
                    <SelectTrigger className="w-32 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9"><SelectValue placeholder="Tier" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="all" className="text-white">All Tiers</SelectItem>
                      <SelectItem value="regular" className="text-white">Regular</SelectItem>
                      <SelectItem value="vip" className="text-white">VIP</SelectItem>
                      <SelectItem value="elite" className="text-white">Elite</SelectItem>
                      <SelectItem value="highCouncil" className="text-white">High Council</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterProduct} onValueChange={setFilterProduct}>
                    <SelectTrigger className="w-36 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9"><SelectValue placeholder="Product" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="all" className="text-white">All Products</SelectItem>
                      <SelectItem value="none" className="text-white">No Product</SelectItem>
                      <SelectItem value="pending" className="text-white">Pending Install</SelectItem>
                      <SelectItem value="installed" className="text-white">Installed</SelectItem>
                      {productList.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {enabledBrokers.map(([key]) => (
                <TabsContent key={key} value={key} className="mt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                          <th className="text-left py-3 px-4 text-[#6B7280] font-semibold text-xs">Client</th>
                          <th className="text-left py-3 px-3 text-[#6B7280] font-semibold text-xs">Account</th>
                          <th className="text-center py-3 px-2 text-[#6B7280] font-semibold text-xs">Tier</th>
                          <th className="text-center py-3 px-2 text-[#6B7280] font-semibold text-xs">Status</th>
                          <th className="text-right py-3 px-3 text-[#6B7280] font-semibold text-xs">Balance</th>
                          <th className="text-right py-3 px-2 text-[#6B7280] font-semibold text-xs">Deposit</th>
                          <th className="text-right py-3 px-2 text-[#6B7280] font-semibold text-xs">Withdraw</th>
                          <th className="text-right py-3 px-2 text-[#6B7280] font-semibold text-xs">Lots</th>
                          <th className="text-center py-3 px-2 text-[#6B7280] font-semibold text-xs">Freq</th>
                          <th className="text-center py-3 px-2 text-[#6B7280] font-semibold text-xs">Product</th>
                          <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedClients.length === 0 ? (
                          <tr><td colSpan={11} className="text-center py-12 text-[#6B7280]"><Database size={32} className="mx-auto mb-2 opacity-50" /><p>No clients found</p></td></tr>
                        ) : (
                          paginatedClients.map((client) => {
                            // Calculate values based on date filter
                            const periodDepo = calcPeriodDeposit(client.transactions, dateRangeValues);
                            const periodWD = calcPeriodWithdraw(client.transactions, dateRangeValues);
                            const periodLots = calcPeriodLots(client.lotEntries, dateRangeValues);
                            const depoFreq = calcDepositFreq(client.transactions);
                            const wdFreq = calcWithdrawFreq(client.transactions);
                            const hasActivity = hasPeriodActivity(client, dateRangeValues);
                            
                            return (
                              <tr key={client.id} className={`border-b border-[hsl(var(--border))] hover:bg-white/5 transition-colors ${!hasActivity && dateRange !== 'all' ? 'opacity-40' : ''}`}>
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="text-white font-medium">{client.name}</p>
                                    <p className="text-xs text-[#6B7280] flex items-center gap-1"><Phone size={10} />{client.phone}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-3">
                                  <p className="text-white font-mono text-xs">{client.accountNo}</p>
                                  <p className="text-[10px] text-[#6B7280]">{client.accountType}</p>
                                </td>
                                <td className="py-3 px-2 text-center"><LoyaltyBadge tier={calculateTier(client)} /></td>
                                <td className="py-3 px-2 text-center"><StatusBadge status={client.status} /></td>
                                <td className="py-3 px-3 text-right"><span className={`font-mono font-semibold ${(client.balance || 0) > 0 ? 'text-white' : 'text-[#E63946]'}`}>${(client.balance || 0).toLocaleString()}</span></td>
                                <td className="py-3 px-2 text-right">
                                  <span className={`font-mono ${periodDepo > 0 ? 'text-[#10B981] font-semibold' : 'text-[#6B7280]'}`}>
                                    {periodDepo > 0 ? `$${periodDepo.toLocaleString()}` : '-'}
                                  </span>
                                </td>
                                <td className="py-3 px-2 text-right">
                                  <span className={`font-mono ${periodWD > 0 ? 'text-[#E63946] font-semibold' : 'text-[#6B7280]'}`}>
                                    {periodWD > 0 ? `$${periodWD.toLocaleString()}` : '-'}
                                  </span>
                                </td>
                                <td className="py-3 px-2 text-right">
                                  <span className={`font-mono ${periodLots > 0 ? 'text-[#8B5CF6] font-semibold' : 'text-[#6B7280]'}`}>
                                    {periodLots > 0 ? periodLots.toFixed(1) : '-'}
                                  </span>
                                </td>
                                <td className="py-3 px-2 text-center">
                                  <div className="flex items-center justify-center gap-1 text-[10px]">
                                    <span className="text-[#10B981]">{depoFreq}D</span>
                                    <span className="text-[#6B7280]">/</span>
                                    <span className="text-[#E63946]">{wdFreq}W</span>
                                  </div>
                                </td>
                                <td className="py-3 px-2 text-center">
                                  {(client.products || []).length > 0 ? (
                                    <div className="flex flex-wrap items-center justify-center gap-1">
                                      {(client.products || []).map(p => (
                                        <Badge key={p.id} className={`text-[9px] px-1.5 py-0 ${
                                          p.status === 'installed' || p.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' :
                                          p.status === 'pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                                          'bg-[#6B7280]/20 text-[#6B7280]'
                                        }`}>{p.product.split(' ')[0]}</Badge>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-[#6B7280]">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#10B981]" onClick={() => openClientProfile(client)} title="View Profile"><Eye size={14} /></Button>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#3B82F6]" onClick={() => { setEditingClient(client); setShowEditModal(true); }}><Edit2 size={14} /></Button>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#E63946]" onClick={() => handleDeleteClient(client.id)}><Trash2 size={14} /></Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {filteredAndSortedClients.length > 0 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-[hsl(var(--border))]">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#6B7280]">Show</span>
                        <Select value={String(itemsPerPage)} onValueChange={(v) => setItemsPerPage(Number(v))}>
                          <SelectTrigger className="w-16 h-8 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                            <SelectItem value="15" className="text-white">15</SelectItem>
                            <SelectItem value="30" className="text-white">30</SelectItem>
                            <SelectItem value="50" className="text-white">50</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-[#6B7280]">of {filteredAndSortedClients.length} clients</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-[hsl(var(--border))] text-white" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                          <ChevronLeft size={16} />
                        </Button>
                        <span className="text-sm text-white px-3">Page {currentPage} of {totalPages || 1}</span>
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-[hsl(var(--border))] text-white" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                          <ChevronRight size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
      
      {/* List Modal with Export */}
      <Dialog open={showListModal} onOpenChange={setShowListModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${listModalType.color}20`, border: `1px solid ${listModalType.color}40` }}>
                  <Users size={22} style={{ color: listModalType.color }} />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-white">{listModalType.title}</DialogTitle>
                  <p className="text-xs text-[#6B7280] mt-0.5">{listModalType.clients?.length || 0} clients</p>
                </div>
              </div>
              {/* Export Buttons */}
              {listModalType.clients?.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[hsl(var(--border))] text-white">
                      <Download size={14} className="mr-2" />Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <DropdownMenuItem className="text-white hover:bg-white/10" onClick={handleExportListToCSV}>
                      <FileSpreadsheet size={14} className="mr-2 text-[#10B981]" />Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-white hover:bg-white/10" onClick={handleExportListToExcel}>
                      <FileSpreadsheet size={14} className="mr-2 text-[#3B82F6]" />Export as Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {listModalType.clients?.length === 0 ? (
              <div className="text-center py-8 text-[#6B7280]"><Users size={40} className="mx-auto mb-2 opacity-50" /><p>No clients</p></div>
            ) : (
              <div className="space-y-2">
                {listModalType.clients?.map((client) => (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3B82F6]/30 to-[#8B5CF6]/20 flex items-center justify-center text-white font-bold">{client.name.charAt(0)}</div>
                      <div>
                        <p className="text-white font-medium">{client.name}</p>
                        <p className="text-xs text-[#6B7280]">{client.accountNo} • {client.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-white font-mono">${(client.balance || 0).toLocaleString()}</p>
                        <p className="text-xs text-[#6B7280]">Balance</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-[#10B981] font-mono">${calcTotalDeposit(client.transactions).toLocaleString()}</p>
                        <p className="text-xs text-[#6B7280]">Total Depo</p>
                      </div>
                      <StatusBadge status={client.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Client Profile Modal with Add Transaction */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3B82F6]/30 to-[#8B5CF6]/20 flex items-center justify-center text-white font-bold text-xl border border-[#3B82F6]/40">{viewingClient?.name?.charAt(0)}</div>
                <div>
                  <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
                    {viewingClient?.name}
                    <LoyaltyBadge tier={viewingClient ? calculateTier(viewingClient) : 'regular'} />
                  </DialogTitle>
                  <p className="text-xs text-[#6B7280] mt-0.5">{viewingClient?.accountNo} • {viewingClient?.phone}</p>
                </div>
              </div>
              <Button size="sm" className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white" onClick={() => setShowAddTransactionModal(true)}>
                <Plus size={14} className="mr-1.5" />Add Entry
              </Button>
            </div>
          </DialogHeader>
          {viewingClient && (
            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <p className="text-[10px] text-[#6B7280] uppercase">Balance</p>
                  <p className="text-lg font-bold text-white font-mono">${(viewingClient.balance || 0).toLocaleString()}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <p className="text-[10px] text-[#6B7280] uppercase">Total Deposit</p>
                  <p className="text-lg font-bold text-[#10B981] font-mono">${calcTotalDeposit(viewingClient.transactions).toLocaleString()}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <p className="text-[10px] text-[#6B7280] uppercase">Total Withdraw</p>
                  <p className="text-lg font-bold text-[#E63946] font-mono">${calcTotalWithdraw(viewingClient.transactions).toLocaleString()}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <p className="text-[10px] text-[#6B7280] uppercase">Total Lots</p>
                  <p className="text-lg font-bold text-[#8B5CF6] font-mono">{calcTotalLots(viewingClient.lotEntries).toFixed(1)}</p>
                </div>
              </div>
              
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-xl border border-[hsl(var(--border))]">
                <div className="flex items-center gap-2 mb-3"><History size={16} className="text-[#3B82F6]" /><p className="text-sm font-semibold text-white">Transaction Frequency</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-[#10B981]/10 rounded-lg border border-[#10B981]/30">
                    <TrendingUp size={24} className="text-[#10B981]" />
                    <div>
                      <p className="text-2xl font-bold text-white font-mono">{calcDepositFreq(viewingClient.transactions)}x</p>
                      <p className="text-xs text-[#10B981]">Deposits</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#E63946]/10 rounded-lg border border-[#E63946]/30">
                    <TrendingDown size={24} className="text-[#E63946]" />
                    <div>
                      <p className="text-2xl font-bold text-white font-mono">{calcWithdrawFreq(viewingClient.transactions)}x</p>
                      <p className="text-xs text-[#E63946]">Withdrawals</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Client Detail Info */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-xl border border-[#06B6D4]/20">
                <div className="flex items-center gap-2 mb-3"><Cpu size={16} className="text-[#06B6D4]" /><p className="text-sm font-semibold text-white">Product & Indicator Info</p></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-2.5 bg-[hsl(var(--surface))]/50 rounded-lg">
                    <p className="text-[10px] text-[#6B7280] uppercase">Client Type</p>
                    <p className="text-sm text-white font-medium capitalize">{viewingClient.clientType || 'new'}</p>
                  </div>
                  <div className="p-2.5 bg-[hsl(var(--surface))]/50 rounded-lg">
                    <p className="text-[10px] text-[#6B7280] uppercase">Username TV</p>
                    <p className="text-sm text-white font-medium">{viewingClient.tvUsername || '-'}</p>
                  </div>
                  <div className="p-2.5 bg-[hsl(var(--surface))]/50 rounded-lg">
                    <p className="text-[10px] text-[#6B7280] uppercase">Telegram ID</p>
                    <p className="text-sm text-white font-medium">{viewingClient.telegramId || '-'}</p>
                  </div>
                  <div className="p-2.5 bg-[hsl(var(--surface))]/50 rounded-lg">
                    <p className="text-[10px] text-[#6B7280] uppercase">Product</p>
                    <p className="text-sm text-[#06B6D4] font-medium">{viewingClient.product || '-'}</p>
                  </div>
                  <div className="p-2.5 bg-[hsl(var(--surface))]/50 rounded-lg">
                    <p className="text-[10px] text-[#6B7280] uppercase">Status Indi</p>
                    <Badge className={`text-[10px] mt-0.5 ${
                      (viewingClient.statusIndi === 'installed' || viewingClient.statusIndi === 'active') ? 'bg-[#10B981]/20 text-[#10B981]' :
                      viewingClient.statusIndi === 'pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                      'bg-[#6B7280]/20 text-[#6B7280]'
                    }`}>{viewingClient.statusIndi || 'pending'}</Badge>
                  </div>
                  <div className="p-2.5 bg-[hsl(var(--surface))]/50 rounded-lg">
                    <p className="text-[10px] text-[#6B7280] uppercase">Account Type</p>
                    <p className="text-sm text-white font-medium">{viewingClient.accountType || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Transaction & Lot History - 4 Tabs */}
              <div>
                <div className="flex items-center gap-2 mb-3"><Activity size={16} className="text-[#F59E0B]" /><p className="text-sm font-semibold text-white">Transaction, Lots & Products</p></div>
                <Tabs defaultValue="products" className="w-full">
                  <TabsList className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] w-full mb-3">
                    <TabsTrigger value="products" className="flex-1 data-[state=active]:bg-[#06B6D4] data-[state=active]:text-white gap-1.5">
                      <Package size={14} />Products ({(viewingClient.products || []).length})
                    </TabsTrigger>
                    <TabsTrigger value="deposits" className="flex-1 data-[state=active]:bg-[#10B981] data-[state=active]:text-white gap-1.5">
                      <TrendingUp size={14} />Depo ({viewingClient.transactions?.filter(t => t.type === 'deposit').length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="withdrawals" className="flex-1 data-[state=active]:bg-[#E63946] data-[state=active]:text-white gap-1.5">
                      <TrendingDown size={14} />WD ({viewingClient.transactions?.filter(t => t.type === 'withdraw').length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="lots" className="flex-1 data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white gap-1.5">
                      <Layers size={14} />Lots ({viewingClient.lotEntries?.length || 0})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="products" className="mt-0">
                    <div className="space-y-2 max-h-[220px] overflow-y-auto">
                      {(viewingClient.products || []).length > 0 ? (
                        (viewingClient.products || []).map((prod) => (
                          <div key={prod.id} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded-lg border border-[hsl(var(--border))]">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                prod.status === 'installed' || prod.status === 'active' ? 'bg-[#10B981]/20' :
                                prod.status === 'pending' ? 'bg-[#F59E0B]/20' : 'bg-[#6B7280]/20'
                              }`}>
                                <Cpu size={16} className={
                                  prod.status === 'installed' || prod.status === 'active' ? 'text-[#10B981]' :
                                  prod.status === 'pending' ? 'text-[#F59E0B]' : 'text-[#6B7280]'
                                } />
                              </div>
                              <div>
                                <p className="text-sm text-white font-medium">{prod.product}</p>
                                <p className="text-xs text-[#6B7280]">TV: {prod.tvUsername || '-'} {prod.notes ? `• ${prod.notes}` : ''}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right mr-2">
                                <Badge className={`text-[10px] ${
                                  prod.status === 'installed' || prod.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' :
                                  prod.status === 'pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                                  'bg-[#6B7280]/20 text-[#6B7280]'
                                }`}>{prod.status}</Badge>
                                <p className="text-[10px] text-[#6B7280] mt-1">{prod.installDate ? new Date(prod.installDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-'}</p>
                              </div>
                              {prod.status === 'pending' && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#10B981]" onClick={() => handleUpdateProductStatus(prod.id, 'installed')} title="Mark Installed">
                                  <CheckCircle2 size={14} />
                                </Button>
                              )}
                              {(prod.status === 'installed' || prod.status === 'active') && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#6B7280]" onClick={() => handleUpdateProductStatus(prod.id, 'expired')} title="Mark Expired">
                                  <XCircle size={14} />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#E63946]" onClick={() => handleRemoveProduct(prod.id)} title="Remove">
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-6 text-[#6B7280]"><Package size={24} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No products assigned</p><p className="text-[10px] mt-1">Click "Add Entry" to assign a product</p></div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="deposits" className="mt-0">
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {viewingClient.transactions?.filter(t => t.type === 'deposit').sort((a, b) => new Date(b.date) - new Date(a.date)).map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded-lg border border-[hsl(var(--border))]">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#10B981]/20">
                              <TrendingUp size={16} className="text-[#10B981]" />
                            </div>
                            <div>
                              <p className="text-sm text-white font-medium">Deposit</p>
                              <p className="text-xs text-[#6B7280]">{entry.note || '-'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-mono font-semibold text-[#10B981]">+${entry.amount?.toLocaleString()}</p>
                            <p className="text-xs text-[#6B7280]">{new Date(entry.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          </div>
                        </div>
                      ))}
                      {!viewingClient.transactions?.filter(t => t.type === 'deposit').length && (
                        <div className="text-center py-6 text-[#6B7280]"><TrendingUp size={24} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No deposits</p></div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="withdrawals" className="mt-0">
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {viewingClient.transactions?.filter(t => t.type === 'withdraw').sort((a, b) => new Date(b.date) - new Date(a.date)).map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded-lg border border-[hsl(var(--border))]">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#E63946]/20">
                              <TrendingDown size={16} className="text-[#E63946]" />
                            </div>
                            <div>
                              <p className="text-sm text-white font-medium">Withdraw</p>
                              <p className="text-xs text-[#6B7280]">{entry.note || '-'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-mono font-semibold text-[#E63946]">-${entry.amount?.toLocaleString()}</p>
                            <p className="text-xs text-[#6B7280]">{new Date(entry.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          </div>
                        </div>
                      ))}
                      {!viewingClient.transactions?.filter(t => t.type === 'withdraw').length && (
                        <div className="text-center py-6 text-[#6B7280]"><TrendingDown size={24} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No withdrawals</p></div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="lots" className="mt-0">
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {viewingClient.lotEntries?.sort((a, b) => new Date(b.date) - new Date(a.date)).map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded-lg border border-[hsl(var(--border))]">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#8B5CF6]/20">
                              <Layers size={16} className="text-[#8B5CF6]" />
                            </div>
                            <div>
                              <p className="text-sm text-white font-medium">Lot Traded</p>
                              <p className="text-xs text-[#6B7280]">{entry.note || '-'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-mono font-semibold text-[#8B5CF6]">{entry.lots} lots</p>
                            <p className="text-xs text-[#6B7280]">{new Date(entry.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          </div>
                        </div>
                      ))}
                      {!viewingClient.lotEntries?.length && (
                        <div className="text-center py-6 text-[#6B7280]"><Layers size={24} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No lot entries</p></div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Transaction/Lot Modal */}
      <Dialog open={showAddTransactionModal} onOpenChange={setShowAddTransactionModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-md">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2"><Plus size={20} className="text-[#3B82F6]" />Add Entry for {viewingClient?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Tabs value={transactionTab} onValueChange={setTransactionTab}>
              <TabsList className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] w-full mb-4">
                <TabsTrigger value="deposit" className="flex-1 data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Deposit</TabsTrigger>
                <TabsTrigger value="withdraw" className="flex-1 data-[state=active]:bg-[#E63946] data-[state=active]:text-white">Withdraw</TabsTrigger>
                <TabsTrigger value="lot" className="flex-1 data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">Lot</TabsTrigger>
                <TabsTrigger value="product" className="flex-1 data-[state=active]:bg-[#06B6D4] data-[state=active]:text-white">Product</TabsTrigger>
              </TabsList>
              
              <TabsContent value="deposit" className="space-y-4">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Amount ($)</Label>
                  <Input type="number" value={newTransaction.amount} onChange={(e) => setNewTransaction({ ...newTransaction, type: 'deposit', amount: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 font-mono" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Date</Label>
                  <Input type="date" value={newTransaction.date} onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Note</Label>
                  <Input value={newTransaction.note} onChange={(e) => setNewTransaction({ ...newTransaction, note: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="e.g., Top up" />
                </div>
              </TabsContent>
              
              <TabsContent value="withdraw" className="space-y-4">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Amount ($)</Label>
                  <Input type="number" value={newTransaction.amount} onChange={(e) => setNewTransaction({ ...newTransaction, type: 'withdraw', amount: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 font-mono" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Date</Label>
                  <Input type="date" value={newTransaction.date} onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Note</Label>
                  <Input value={newTransaction.note} onChange={(e) => setNewTransaction({ ...newTransaction, note: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="e.g., Profit withdrawal" />
                </div>
              </TabsContent>
              
              <TabsContent value="lot" className="space-y-4">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Lots Traded</Label>
                  <Input type="number" step="0.01" value={newLotEntry.lots} onChange={(e) => setNewLotEntry({ ...newLotEntry, lots: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 font-mono" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Date</Label>
                  <Input type="date" value={newLotEntry.date} onChange={(e) => setNewLotEntry({ ...newLotEntry, date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Note</Label>
                  <Input value={newLotEntry.note} onChange={(e) => setNewLotEntry({ ...newLotEntry, note: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="e.g., Daily trading" />
                </div>
              </TabsContent>
              
              <TabsContent value="product" className="space-y-4">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Product / Indicator</Label>
                  <Select value={newProductAssignment.product} onValueChange={(v) => setNewProductAssignment({ ...newProductAssignment, product: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      {productList.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">TradingView Username</Label>
                  <Input value={newProductAssignment.tvUsername} onChange={(e) => setNewProductAssignment({ ...newProductAssignment, tvUsername: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="e.g., trader123" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-[#6B7280]">Status</Label>
                    <Select value={newProductAssignment.status} onValueChange={(v) => setNewProductAssignment({ ...newProductAssignment, status: v })}>
                      <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        <SelectItem value="pending" className="text-white">Pending</SelectItem>
                        <SelectItem value="installed" className="text-white">Installed</SelectItem>
                        <SelectItem value="active" className="text-white">Active</SelectItem>
                        <SelectItem value="recheck_tv" className="text-white">Recheck TV</SelectItem>
                        <SelectItem value="remove_access" className="text-white">Remove Access</SelectItem>
                        <SelectItem value="remove" className="text-white">Remove</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#6B7280]">Install Date</Label>
                    <Input type="date" value={newProductAssignment.installDate} onChange={(e) => setNewProductAssignment({ ...newProductAssignment, installDate: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Notes</Label>
                  <Input value={newProductAssignment.notes} onChange={(e) => setNewProductAssignment({ ...newProductAssignment, notes: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="e.g., Chart H4, pair XAUUSD" />
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowAddTransactionModal(false)} className="border-[hsl(var(--border))] text-white">Cancel</Button>
            <Button 
              onClick={transactionTab === 'lot' ? handleAddLotEntry : transactionTab === 'product' ? handleAddProductToClient : handleAddTransaction} 
              className={`text-white ${transactionTab === 'deposit' ? 'bg-[#10B981]' : transactionTab === 'withdraw' ? 'bg-[#E63946]' : transactionTab === 'product' ? 'bg-[#06B6D4]' : 'bg-[#8B5CF6]'}`}
            >
              <Check size={16} className="mr-2" />Add {transactionTab === 'lot' ? 'Lot Entry' : transactionTab === 'deposit' ? 'Deposit' : transactionTab === 'product' ? 'Product' : 'Withdraw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2"><Users size={20} className="text-[#3B82F6]" />Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-[10px] text-[#6B7280]">Name *</Label><Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
              <div><Label className="text-[10px] text-[#6B7280]">Phone</Label><Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-[10px] text-[#6B7280]">Account No *</Label><Input value={newClient.accountNo} onChange={(e) => setNewClient({ ...newClient, accountNo: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
              <div><Label className="text-[10px] text-[#6B7280]">Account Type</Label>
                <Select value={newClient.accountType} onValueChange={(v) => setNewClient({ ...newClient, accountType: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="Standard" className="text-white">Standard</SelectItem>
                    <SelectItem value="Pro" className="text-white">Pro</SelectItem>
                    <SelectItem value="Premium" className="text-white">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] text-[#6B7280]">Status</Label>
                <Select value={newClient.status} onValueChange={(v) => setNewClient({ ...newClient, status: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="active" className="text-white">Active</SelectItem>
                    <SelectItem value="noTrade" className="text-white">No Trade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-[10px] text-[#6B7280]">Initial Deposit ($)</Label><Input type="number" value={newClient.initialDeposit} onChange={(e) => setNewClient({ ...newClient, initialDeposit: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 font-mono" /></div>
              <div><Label className="text-[10px] text-[#6B7280]">Balance ($)</Label><Input type="number" value={newClient.balance} onChange={(e) => setNewClient({ ...newClient, balance: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 font-mono" /></div>
              <div><Label className="text-[10px] text-[#6B7280]">Reg Date</Label><Input type="date" value={newClient.regDate} onChange={(e) => setNewClient({ ...newClient, regDate: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" /></div>
            </div>
            {/* New Fields */}
            <p className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold flex items-center gap-2 pt-2"><Cpu size={14} className="text-[#06B6D4]" />Product & Indicator Info</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] text-[#6B7280]">Client Type</Label>
                <Select value={newClient.clientType} onValueChange={(v) => setNewClient({ ...newClient, clientType: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="new" className="text-white">New</SelectItem>
                    <SelectItem value="existing" className="text-white">Existing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] text-[#6B7280]">Username TV</Label><Input value={newClient.tvUsername} onChange={(e) => setNewClient({ ...newClient, tvUsername: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="TradingView username" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-[10px] text-[#6B7280]">Telegram ID</Label><Input value={newClient.telegramId} onChange={(e) => setNewClient({ ...newClient, telegramId: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" placeholder="@username" /></div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Product</Label>
                <Select value={newClient.product} onValueChange={(v) => setNewClient({ ...newClient, product: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="none" className="text-white">-</SelectItem>
                    {productList.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Status Indi</Label>
                <Select value={newClient.statusIndi} onValueChange={(v) => setNewClient({ ...newClient, statusIndi: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="pending" className="text-white">Pending</SelectItem>
                    <SelectItem value="installed" className="text-white">Installed</SelectItem>
                    <SelectItem value="active" className="text-white">Active</SelectItem>
                    <SelectItem value="recheck_tv" className="text-white">Recheck TV</SelectItem>
                    <SelectItem value="remove_access" className="text-white">Remove Access</SelectItem>
                    <SelectItem value="remove" className="text-white">Remove</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-[hsl(var(--border))] text-white">Cancel</Button>
            <Button onClick={handleAddClient} className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white"><Check size={16} className="mr-2" />Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Client Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-lg">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2"><Edit2 size={20} className="text-[#F59E0B]" />Edit Client</DialogTitle>
          </DialogHeader>
          {editingClient && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-[10px] text-[#6B7280]">Name</Label><Input value={editingClient.name} onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
                <div><Label className="text-[10px] text-[#6B7280]">Phone</Label><Input value={editingClient.phone} onChange={(e) => setEditingClient({ ...editingClient, phone: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-[10px] text-[#6B7280]">Balance ($)</Label><Input type="number" value={editingClient.balance} onChange={(e) => setEditingClient({ ...editingClient, balance: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 font-mono" /></div>
                <div><Label className="text-[10px] text-[#6B7280]">Status</Label>
                  <Select value={editingClient.status} onValueChange={(v) => setEditingClient({ ...editingClient, status: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="active" className="text-white">Active</SelectItem>
                      <SelectItem value="noTrade" className="text-white">No Trade</SelectItem>
                      <SelectItem value="mc" className="text-white">MC</SelectItem>
                      <SelectItem value="loss" className="text-white">Loss</SelectItem>
                      <SelectItem value="growing" className="text-white">Growing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Product & Indicator Fields */}
              <p className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold flex items-center gap-2 pt-2"><Cpu size={14} className="text-[#06B6D4]" />Product & Indicator Info</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Client Type</Label>
                  <Select value={editingClient.clientType || 'new'} onValueChange={(v) => setEditingClient({ ...editingClient, clientType: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="new" className="text-white">New</SelectItem>
                      <SelectItem value="existing" className="text-white">Existing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px] text-[#6B7280]">Username TV</Label><Input value={editingClient.tvUsername || ''} onChange={(e) => setEditingClient({ ...editingClient, tvUsername: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-[10px] text-[#6B7280]">Telegram ID</Label><Input value={editingClient.telegramId || ''} onChange={(e) => setEditingClient({ ...editingClient, telegramId: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" /></div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Product</Label>
                  <Select value={editingClient.product || 'none'} onValueChange={(v) => setEditingClient({ ...editingClient, product: v === 'none' ? '' : v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="none" className="text-white">-</SelectItem>
                      {productList.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Status Indi</Label>
                  <Select value={editingClient.statusIndi || 'pending'} onValueChange={(v) => setEditingClient({ ...editingClient, statusIndi: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="pending" className="text-white">Pending</SelectItem>
                      <SelectItem value="installed" className="text-white">Installed</SelectItem>
                      <SelectItem value="active" className="text-white">Active</SelectItem>
                      <SelectItem value="recheck_tv" className="text-white">Recheck TV</SelectItem>
                      <SelectItem value="remove_access" className="text-white">Remove Access</SelectItem>
                      <SelectItem value="remove" className="text-white">Remove</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-[hsl(var(--border))] text-white">Cancel</Button>
            <Button onClick={handleEditClient} className="bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white"><Check size={16} className="mr-2" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settings Modal - Updated: Super Elite -> High Council */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2"><Settings size={20} className="text-[#8B5CF6]" />Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Product List Management */}
            <div>
              <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Package size={16} className="text-[#06B6D4]" />Product / Indicator List</p>
              <div className="space-y-2 mb-3">
                {productList.map((product, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2">
                      <Cpu size={14} className="text-[#06B6D4]" />
                      <span className="text-sm text-white">{product}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#E63946]" onClick={() => setProductList(prev => prev.filter((_, i) => i !== idx))}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input 
                  value={newProductName} 
                  onChange={(e) => setNewProductName(e.target.value)}
                  placeholder="New product name..." 
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white text-sm h-8" 
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newProductName.trim()) {
                      setProductList(prev => [...prev, newProductName.trim()]);
                      setNewProductName('');
                    }
                  }}
                />
                <Button size="sm" className="h-8 bg-[#06B6D4] text-white" onClick={() => {
                  if (newProductName.trim()) {
                    setProductList(prev => [...prev, newProductName.trim()]);
                    setNewProductName('');
                  }
                }}><Plus size={14} /></Button>
              </div>
            </div>
            
            <div>
              <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Crown size={16} className="text-[#F59E0B]" />Loyalty Tiers</p>
              <div className="space-y-3">
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#F59E0B] mb-2">VIP</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px] text-[#6B7280]">Min Deposit</Label><Input type="number" value={tierSettings.vip.minDeposit} onChange={(e) => setTierSettings({ ...tierSettings, vip: { ...tierSettings.vip, minDeposit: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                    <div><Label className="text-[10px] text-[#6B7280]">Min Lots</Label><Input type="number" value={tierSettings.vip.minLots} onChange={(e) => setTierSettings({ ...tierSettings, vip: { ...tierSettings.vip, minLots: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                    <div><Label className="text-[10px] text-[#6B7280]">Min WD</Label><Input type="number" value={tierSettings.vip.minWithdraw} onChange={(e) => setTierSettings({ ...tierSettings, vip: { ...tierSettings.vip, minWithdraw: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                  </div>
                </div>
                <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#8B5CF6] mb-2">Elite</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px] text-[#6B7280]">Min Deposit</Label><Input type="number" value={tierSettings.elite.minDeposit} onChange={(e) => setTierSettings({ ...tierSettings, elite: { ...tierSettings.elite, minDeposit: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                    <div><Label className="text-[10px] text-[#6B7280]">Min Lots</Label><Input type="number" value={tierSettings.elite.minLots} onChange={(e) => setTierSettings({ ...tierSettings, elite: { ...tierSettings.elite, minLots: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                    <div><Label className="text-[10px] text-[#6B7280]">Min WD</Label><Input type="number" value={tierSettings.elite.minWithdraw} onChange={(e) => setTierSettings({ ...tierSettings, elite: { ...tierSettings.elite, minWithdraw: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                  </div>
                </div>
                <div className="bg-[#E63946]/10 border border-[#E63946]/30 rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#E63946] mb-2">High Council</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px] text-[#6B7280]">Min Deposit</Label><Input type="number" value={tierSettings.highCouncil.minDeposit} onChange={(e) => setTierSettings({ ...tierSettings, highCouncil: { ...tierSettings.highCouncil, minDeposit: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                    <div><Label className="text-[10px] text-[#6B7280]">Min Lots</Label><Input type="number" value={tierSettings.highCouncil.minLots} onChange={(e) => setTierSettings({ ...tierSettings, highCouncil: { ...tierSettings.highCouncil, minLots: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                    <div><Label className="text-[10px] text-[#6B7280]">Min WD</Label><Input type="number" value={tierSettings.highCouncil.minWithdraw} onChange={(e) => setTierSettings({ ...tierSettings, highCouncil: { ...tierSettings.highCouncil, minWithdraw: Number(e.target.value) } })} className="h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" /></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><MessageCircle size={16} className="text-[#3B82F6]" />PM Criteria</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <Label className="text-[10px] text-[#6B7280]">Loss Threshold %</Label>
                  <Input type="number" value={pmCriteria.lossThreshold} onChange={(e) => setPMCriteria({ ...pmCriteria, lossThreshold: Number(e.target.value) })} className="h-8 mt-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" />
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <Label className="text-[10px] text-[#6B7280]">No Trade Days</Label>
                  <Input type="number" value={pmCriteria.noTradeDays} onChange={(e) => setPMCriteria({ ...pmCriteria, noTradeDays: Number(e.target.value) })} className="h-8 mt-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" />
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <Label className="text-[10px] text-[#6B7280]">Growth %</Label>
                  <Input type="number" value={pmCriteria.growthThreshold} onChange={(e) => setPMCriteria({ ...pmCriteria, growthThreshold: Number(e.target.value) })} className="h-8 mt-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs font-mono" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button onClick={() => setShowSettingsModal(false)} className="bg-gradient-to-r from-[#8B5CF6] to-[#E63946] text-white"><Check size={16} className="mr-2" />Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
